import { useEffect, useRef, useState } from 'react';
import { SbIcon } from './sidebarIcons';

/**
 * "A report" against "NyumbaSuite", on the same seven tenants.
 *
 * Illustrative figures, labelled as such by the section around it. Every
 * behaviour the NyumbaSuite side shows exists today: arrears banded by age,
 * oldest first, a tenant with no contact flagged rather than skipped, and one
 * action that sends every tenant a reminder carrying their own balance. The one
 * thing not built yet stays outside this panel, in the strip beneath it.
 *
 * The switch flips on its own the first time the panel scrolls into view, so the
 * point is made without a click, and stays under the visitor's control after.
 * For anyone who asked for reduced motion it opens on the answer, with no
 * count-up and no growing bars.
 */
interface Row { unit: string; days: number; kes: number; noContact?: boolean }
const ROWS: Row[] = [
  { unit: 'A4', days: 5, kes: 18000 },
  { unit: 'C1', days: 9, kes: 24000 },
  { unit: 'B7', days: 14, kes: 31500 },
  { unit: 'D3', days: 21, kes: 29000, noContact: true },
  { unit: 'A9', days: 27, kes: 36500 },
  { unit: 'B2', days: 73, kes: 40000 },
  { unit: 'C6', days: 91, kes: 35500 },
];
const BANDS = [
  { key: 'old', label: '60+ days', warn: true, rows: ROWS.filter((r) => r.days >= 60) },
  { key: 'mid', label: '8 to 30 days', warn: false, rows: ROWS.filter((r) => r.days >= 8 && r.days < 60) },
  { key: 'new', label: '1 to 7 days', warn: false, rows: ROWS.filter((r) => r.days < 8) },
];
const sum = (rows: Row[]) => rows.reduce((n, r) => n + r.kes, 0);
const MAX = Math.max(...BANDS.map((b) => sum(b.rows)));
const kes = (n: number) => `KES ${Math.round(n).toLocaleString('en-KE')}`;
const reduced = () => !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** Counts up to `to` once `run` is true; jumps straight there under reduced motion. */
function useCount(to: number, run: boolean): number {
  const [v, setV] = useState(() => (reduced() ? to : 0));
  useEffect(() => {
    if (!run) return;
    if (reduced()) { setV(to); return; }
    let raf = 0; const t0 = performance.now(); const dur = 900;
    const tick = (t: number) => {
      const p = Math.min(1, (t - t0) / dur);
      setV(to * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [to, run]);
  return v;
}

function BandBar({ band, on, open, onToggle }: {
  band: typeof BANDS[number]; on: boolean; open: boolean; onToggle: () => void;
}) {
  const total = sum(band.rows);
  const shown = useCount(total, on);
  return (
    <div className={`ans-band${band.warn ? ' warn' : ''}${open ? ' open' : ''}`}>
      <button type="button" className="ans-band-head" aria-expanded={open} onClick={onToggle}>
        <span className="ans-band-name">{band.rows.length} tenant{band.rows.length === 1 ? '' : 's'}, {band.label}</span>
        <span className="ans-band-amt">{kes(shown)}</span>
        <span className="ans-band-chev" aria-hidden="true"><SbIcon name="chevron" size={16} /></span>
      </button>
      <div className="ans-track" aria-hidden="true">
        <span className="ans-fill" style={{ width: on ? `${(total / MAX) * 100}%` : '0%' }} />
      </div>
      {open && (
        <ul className="ans-detail">
          {band.rows.map((r) => (
            <li key={r.unit}>
              <span>Unit {r.unit}</span>
              <span className="ans-muted">{r.days} days</span>
              <span className="ans-num">{kes(r.kes)}</span>
              {r.noContact && <span className="ans-flag">No phone or email, flagged</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function ArrearsAnswer() {
  const [mode, setMode] = useState<'report' | 'suite'>(() => (reduced() ? 'suite' : 'report'));
  const [open, setOpen] = useState<string>('old');
  const [msg, setMsg] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const touched = useRef(false);

  // First time it is on screen, move to the answer on its own.
  useEffect(() => {
    const el = box.current;
    if (!el || mode === 'suite' || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((es) => {
      if (es[0]?.isIntersecting && !touched.current) {
        window.setTimeout(() => { if (!touched.current) setMode('suite'); }, 900);
        io.disconnect();
      }
    }, { threshold: 0.45 });
    io.observe(el);
    return () => io.disconnect();
  }, [mode]);

  const choose = (m: 'report' | 'suite') => { touched.current = true; setMode(m); };
  const total = sum(ROWS);

  return (
    <div className="ans" ref={box}>
      <div className="ans-switch" role="tablist" aria-label="Compare a report with NyumbaSuite">
        <button type="button" role="tab" aria-selected={mode === 'report'} className={mode === 'report' ? 'on' : ''}
          onClick={() => choose('report')}>A report</button>
        <button type="button" role="tab" aria-selected={mode === 'suite'} className={mode === 'suite' ? 'on' : ''}
          onClick={() => choose('suite')}>NyumbaSuite</button>
      </div>

      {mode === 'report' ? (
        <div className="ans-panel plain" role="tabpanel">
          <p className="ans-lead">7 tenants are in arrears. {kes(total)} outstanding.</p>
          <table className="ans-table">
            <thead><tr><th>Unit</th><th>Days</th><th className="r">Balance</th></tr></thead>
            <tbody>
              {[...ROWS].sort((a, b) => a.unit.localeCompare(b.unit)).map((r) => (
                <tr key={r.unit}><td>{r.unit}</td><td>{r.days}</td><td className="r">{r.kes.toLocaleString('en-KE')}</td></tr>
              ))}
            </tbody>
          </table>
          <p className="ans-muted ans-after">Then you work out the order, and who to call, yourself.</p>
        </div>
      ) : (
        <div className="ans-panel smart" role="tabpanel">
          <p className="ans-lead">Oldest first, so you chase the two that matter.</p>
          {BANDS.map((b) => (
            <BandBar key={b.key} band={b} on={mode === 'suite'} open={open === b.key}
              onToggle={() => setOpen(open === b.key ? '' : b.key)} />
          ))}
          <div className="ans-send">
            <button type="button" className="btn btn-secondary btn-sm" aria-expanded={msg} onClick={() => setMsg(!msg)}>
              {msg ? 'Hide the reminder' : 'See the reminder each tenant gets'}
            </button>
            {msg && (
              <div className="ans-sms">
                <p>Reminder: your rent balance is <strong>KES 40,000</strong>. Please pay by M-Pesa.</p>
                <span>One action sends this to all of them, each with their own balance. A tenant with no phone or email is flagged, not skipped.</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
